/**
 * Protocolo do gateway WebSocket. Ver docs/03-gateway-protocol.md.
 *
 * Este ficheiro é o contrato: o servidor valida input com estes schemas e os
 * clientes derivam tipos deles. Uma fonte, zero drift.
 */
import { z } from 'zod';

export const GATEWAY_VERSION = 1;

/** 41250ms — com jitter no primeiro intervalo, para não ter todos os
 *  clientes a bater no mesmo segundo após um reinício. */
export const HEARTBEAT_INTERVAL_MS = 41_250;

/** Eventos guardados por sessão para RESUME. */
export const RESUME_BUFFER_SIZE = 500;

/** Tempo que uma sessão sobrevive desconectada antes de deixar de ser retomável. */
export const SESSION_TTL_MS = 5 * 60_000;

// ─── Opcodes ─────────────────────────────────────────────────────────────────

export const Op = {
  DISPATCH: 0,
  HEARTBEAT: 1,
  HEARTBEAT_ACK: 2,
  HELLO: 3,
  IDENTIFY: 4,
  RESUME: 5,
  RECONNECT: 6,
  INVALID_SESSION: 7,
  PRESENCE_UPDATE: 8,
  VOICE_STATE_UPDATE: 9,
  SUBSCRIBE: 10,
  REQUEST_MEMBERS: 11,
  SPEAKING_UPDATE: 12,
} as const;

export type Opcode = (typeof Op)[keyof typeof Op];

/** Opcodes que o cliente pode enviar. Tudo o resto fecha com INVALID_OPCODE. */
export const CLIENT_OPCODES: readonly Opcode[] = [
  Op.HEARTBEAT,
  Op.IDENTIFY,
  Op.RESUME,
  Op.PRESENCE_UPDATE,
  Op.VOICE_STATE_UPDATE,
  Op.SUBSCRIBE,
  Op.REQUEST_MEMBERS,
  Op.SPEAKING_UPDATE,
];

// ─── Códigos de fecho ────────────────────────────────────────────────────────

export const CloseCode = {
  UNKNOWN_ERROR: 4000,
  INVALID_OPCODE: 4001,
  INVALID_PAYLOAD: 4002,
  NOT_AUTHENTICATED: 4003,
  AUTHENTICATION_FAILED: 4004,
  ALREADY_AUTHENTICATED: 4005,
  INVALID_SEQUENCE: 4007,
  RATE_LIMITED: 4008,
  SESSION_EXPIRED: 4009,
} as const;

export type CloseCodeValue = (typeof CloseCode)[keyof typeof CloseCode];

/**
 * Códigos após os quais o cliente NÃO deve reconectar. Reconectar em loop
 * com um token morto é como se derruba o próprio servidor.
 */
export const NON_RECOVERABLE_CLOSE_CODES: readonly CloseCodeValue[] = [
  CloseCode.NOT_AUTHENTICATED,
  CloseCode.AUTHENTICATION_FAILED,
  CloseCode.INVALID_SEQUENCE,
  CloseCode.SESSION_EXPIRED,
];

// ─── Nomes de eventos ────────────────────────────────────────────────────────

export const EventName = {
  READY: 'READY',
  RESUMED: 'RESUMED',

  GUILD_CREATE: 'GUILD_CREATE',
  GUILD_UPDATE: 'GUILD_UPDATE',
  GUILD_DELETE: 'GUILD_DELETE',
  GUILD_MEMBER_ADD: 'GUILD_MEMBER_ADD',
  GUILD_MEMBER_UPDATE: 'GUILD_MEMBER_UPDATE',
  GUILD_MEMBER_REMOVE: 'GUILD_MEMBER_REMOVE',
  GUILD_MEMBERS_CHUNK: 'GUILD_MEMBERS_CHUNK',
  GUILD_ROLE_CREATE: 'GUILD_ROLE_CREATE',
  GUILD_ROLE_UPDATE: 'GUILD_ROLE_UPDATE',
  GUILD_ROLE_DELETE: 'GUILD_ROLE_DELETE',
  GUILD_BAN_ADD: 'GUILD_BAN_ADD',
  GUILD_BAN_REMOVE: 'GUILD_BAN_REMOVE',
  GUILD_EMOJIS_UPDATE: 'GUILD_EMOJIS_UPDATE',

  CHANNEL_CREATE: 'CHANNEL_CREATE',
  CHANNEL_UPDATE: 'CHANNEL_UPDATE',
  CHANNEL_DELETE: 'CHANNEL_DELETE',
  CHANNEL_PINS_UPDATE: 'CHANNEL_PINS_UPDATE',

  MESSAGE_CREATE: 'MESSAGE_CREATE',
  MESSAGE_UPDATE: 'MESSAGE_UPDATE',
  MESSAGE_DELETE: 'MESSAGE_DELETE',
  MESSAGE_DELETE_BULK: 'MESSAGE_DELETE_BULK',
  MESSAGE_REACTION_ADD: 'MESSAGE_REACTION_ADD',
  MESSAGE_REACTION_REMOVE: 'MESSAGE_REACTION_REMOVE',
  MESSAGE_REACTION_REMOVE_ALL: 'MESSAGE_REACTION_REMOVE_ALL',
  MESSAGE_ACK: 'MESSAGE_ACK',

  PRESENCE_UPDATE: 'PRESENCE_UPDATE',
  TYPING_START: 'TYPING_START',
  VOICE_STATE_UPDATE: 'VOICE_STATE_UPDATE',
  VOICE_SERVER_UPDATE: 'VOICE_SERVER_UPDATE',
  SPEAKING_UPDATE: 'SPEAKING_UPDATE',

  USER_UPDATE: 'USER_UPDATE',
  RELATIONSHIP_ADD: 'RELATIONSHIP_ADD',
  RELATIONSHIP_REMOVE: 'RELATIONSHIP_REMOVE',
} as const;

export type EventNameValue = (typeof EventName)[keyof typeof EventName];

/**
 * Eventos efémeros: NÃO entram no buffer de RESUME. Reentregar indicadores
 * de escrita de há quatro minutos é pior que perdê-los.
 */
export const EPHEMERAL_EVENTS: readonly EventNameValue[] = [
  EventName.TYPING_START,
  EventName.PRESENCE_UPDATE,
  EventName.SPEAKING_UPDATE,
];

// ─── Envelope ────────────────────────────────────────────────────────────────

const snowflakeString = z
  .string()
  .regex(/^(?:0|[1-9]\d{0,19})$/, 'snowflake inválido');

export const frameSchema = z.object({
  op: z.number().int().min(0).max(12),
  d: z.unknown().optional(),
  s: z.number().int().nonnegative().optional(),
  t: z.string().optional(),
});

export type Frame = z.infer<typeof frameSchema>;

// ─── Payloads do cliente ─────────────────────────────────────────────────────

export const identifySchema = z.object({
  token: z.string().min(1).max(2048),
  capabilities: z.number().int().nonnegative().default(0),
  properties: z
    .object({
      os: z.string().max(64).optional(),
      client: z.string().max(64).optional(),
      version: z.string().max(32).optional(),
    })
    .default({}),
});

export const resumeSchema = z.object({
  token: z.string().min(1).max(2048),
  sessionId: snowflakeString,
  s: z.number().int().nonnegative(),
});

export const heartbeatSchema = z.object({
  s: z.number().int().nonnegative().nullable(),
});

export const presenceStatusSchema = z.enum(['online', 'idle', 'dnd', 'invisible']);
export type PresenceStatus = z.infer<typeof presenceStatusSchema>;

export const presenceUpdateSchema = z.object({
  status: presenceStatusSchema,
  activity: z
    .object({
      type: z.enum(['playing', 'listening', 'watching', 'custom']),
      name: z.string().max(128),
    })
    .nullish(),
});

export const voiceStateUpdateSchema = z.object({
  channelId: snowflakeString.nullable(),
  selfMute: z.boolean().default(false),
  selfDeaf: z.boolean().default(false),
  selfVideo: z.boolean().default(false),
  selfStream: z.boolean().default(false),
});

/**
 * Deteção de fala. O cliente envia só MUDANÇAS de estado, nunca por frame —
 * ver docs/05. É o anel verde na UI e nada mais: o que controla o envio de
 * media é o DTX do Opus.
 */
export const speakingUpdateSchema = z.object({
  speaking: z.boolean(),
});

export const subscribeSchema = z.object({
  guildId: snowflakeString,
  channelIds: z.array(snowflakeString).max(100).default([]),
  memberRange: z.tuple([z.number().int().min(0), z.number().int().min(0)]).optional(),
});

export const requestMembersSchema = z.object({
  guildId: snowflakeString,
  query: z.string().max(64).default(''),
  limit: z.number().int().min(1).max(100).default(50),
});

/** Mapa opcode → schema, para um único ponto de validação no servidor. */
export const CLIENT_PAYLOAD_SCHEMAS = {
  [Op.HEARTBEAT]: heartbeatSchema,
  [Op.IDENTIFY]: identifySchema,
  [Op.RESUME]: resumeSchema,
  [Op.PRESENCE_UPDATE]: presenceUpdateSchema,
  [Op.VOICE_STATE_UPDATE]: voiceStateUpdateSchema,
  [Op.SUBSCRIBE]: subscribeSchema,
  [Op.REQUEST_MEMBERS]: requestMembersSchema,
  [Op.SPEAKING_UPDATE]: speakingUpdateSchema,
} as const;

// ─── Payloads do servidor ────────────────────────────────────────────────────

export interface HelloPayload {
  heartbeatIntervalMs: number;
  sessionId: string;
}

export interface InvalidSessionPayload {
  resumable: boolean;
}

export interface ReadyPayload {
  v: number;
  sessionId: string;
  user: unknown;
  /** Apenas ids — cada guild é preenchido por um GUILD_CREATE assíncrono. */
  guilds: Array<{ id: string; unavailable: true }>;
  privateChannels: unknown[];
  readStates: unknown[];
  relationships: unknown[];
  resumeGatewayUrl: string;
}

// ─── Rate limits ─────────────────────────────────────────────────────────────

export const GATEWAY_RATE_LIMITS = {
  frames: { limit: 120, windowMs: 60_000 },
  identify: { limit: 1, windowMs: 5_000 },
  presenceUpdate: { limit: 5, windowMs: 20_000 },
  voiceStateUpdate: { limit: 5, windowMs: 20_000 },
  requestMembers: { limit: 10, windowMs: 60_000 },
  /**
   * Fala: generoso porque são mudanças de estado numa conversa real, e o
   * excedente é DESCARTADO em silêncio em vez de fechar o socket — perder um
   * anel verde é melhor que atirar alguém fora da voz. O cliente já impõe um
   * mínimo de 1s entre envios.
   */
  speakingUpdate: { limit: 40, windowMs: 20_000 },
  connectionsPerUser: 8,
} as const;

// ─── Backoff de reconexão ────────────────────────────────────────────────────

export const RECONNECT_BASE_MS = 1_000;
export const RECONNECT_MAX_MS = 30_000;

/** 1s, 2s, 4s, 8s, 16s, 30s… com jitter de 0-30%. */
export function reconnectDelay(attempt: number, random = Math.random): number {
  const base = Math.min(RECONNECT_BASE_MS * 2 ** Math.max(0, attempt - 1), RECONNECT_MAX_MS);
  return Math.round(base * (1 + random() * 0.3));
}

export function shouldReconnect(closeCode: number): boolean {
  return !NON_RECOVERABLE_CLOSE_CODES.includes(closeCode as CloseCodeValue);
}
